import { Card, CardContent, CardHeader, CardTitle, Input, Label } from "../../../../components";
import InlineAlert from "../../../../components/common/InlineAlert";
import type { UserCreateRequest, UserUpdateRequest } from "../../types/user.types";
import { Box, Tab, Tabs } from "@mui/material";
import { useState } from "react";

export type UserFormValues = UserCreateRequest & UserUpdateRequest;
type Props = {
  formId: string;
  values: UserFormValues;
  error?: string | null;
  loading?: boolean;
  mode: "create" | "update";
  onChange: (values: UserFormValues) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
};

export function UserForm({
  formId,
  values,
  error,
  loading = false,
  mode,
  onChange,
  onSubmit,
}: Props) {
  const [activeTab, setActiveTab] = useState(0);
  const set = (key: keyof UserFormValues, value: string) => onChange({ ...values, [key]: value });
  const field = (key: keyof UserFormValues, label: string, type = "text") => (
    <div className="space-y-2">
      <Label htmlFor={`${formId}-${String(key)}`}>{label}</Label>
      <Input
        id={`${formId}-${String(key)}`}
        type={type}
        disabled={loading}
        value={String(values[key] ?? "")}
        onChange={(event) => set(key, event.target.value)}
      />
    </div>
  );
  return (
    <form
      id={formId}
      onSubmit={onSubmit}
      aria-label="User form"
      className="mx-auto max-w-5xl space-y-6 p-6"
    >
      {error ? <InlineAlert message={error} /> : null}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={activeTab} onChange={(_, value: number) => setActiveTab(value)} aria-label="User form sections">
          <Tab label="Basic information" />
          <Tab label="Organization" />
        </Tabs>
      </Box>
      {activeTab === 0 && mode === "create" ? (
        <Card>
          <CardHeader>
            <CardTitle>Account credentials</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {field("email", "Email", "email")} {field("phone_number", "Phone number")} {field("password", "Password", "password")}
          </CardContent>
        </Card>
      ) : null}
      {activeTab === 0 ? <Card>
        <CardHeader>
          <CardTitle>Personal information</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {field("first_name", "First name")} {field("last_name", "Last name")}{" "}
          {field("country_code", "Country code")} {field("country", "Country")}{" "}
          {field("zip_code", "ZIP code")} {mode === "update" ? field("phone_number", "Phone number") : null}{" "}
          {field("gender", "Gender (male/female/other)")} {field("date_of_birth", "Date of birth", "date")}{" "}
          {field("age", "Age", "number")} {field("language", "Language (english/vietnamese)")}
        </CardContent>
      </Card> : null}
      {activeTab === 1 ? <Card>
        <CardHeader>
          <CardTitle>Organization</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {field("population_id", "Population ID")}
        </CardContent>
      </Card> : null}
    </form>
  );
}
